﻿import { useState, useEffect } from "react";
import { CrudFunctions } from "../../utilities/crud-functions";
import { DynamicsItemBase } from '../../models/Dynamics/dynamics-item-base';
import { DynamicsItem } from '../../models/Dynamics/dynamics-item';
import { DynamicsCustomerBase } from '../../models/Dynamics/dynamics-customer-base';
import { DynamicsCustomer } from '../../models/Dynamics/dynamics-customer';
import { DynamicsUnitOfMeasureBase } from '../../models/Dynamics/units-of-measure-base';
import { DynamicsUnitOfMeasure } from '../../models/Dynamics/units-of-measure';
import { DynamicsItemCategoryBase } from '../../models/Dynamics/dynamics-item-category-base';
import { DynamicsItemCategory } from '../../models/Dynamics/dynamics-item-category';
import { ToastProps } from '../../models/toast-props';

import DataGrid, {
    Column,
    Toolbar,
    Button as ColumnButton,
    FilterRow,
    Selection,
    HeaderFilter,
    Item as ToolbarItem,
    Lookup,
    Editing,
    Paging,
    Pager,
    SearchPanel
} from 'devextreme-react/data-grid';

import Tabs, { Item as TabItem } from 'devextreme-react/tabs';
import { Toast } from 'devextreme-react/toast';
import { Button } from "devextreme-react/button";
import { TextBox } from "devextreme-react/text-box";
import { SelectBox } from "devextreme-react/select-box";

const tabsConfig = [
    {
        id: 0,
        text: 'Items',
        icon: 'info',
    },
    {
        id: 1,
        text: 'Customers',
        icon: 'overflow',
    },
    {
        id: 2,
        text: 'Opportunities',
        icon: 'eyeopen',
    },
    {
        id: 3,
        text: 'UOMs',
        icon: 'overflow',
    },
    {
        id: 4,
        text: 'Item Categories',
        icon: 'overflow',
    },
];

export enum HeaderTabType {
    Items = 0,
    Customers = 1,
    Opportunities = 2,
    Uoms = 3,
    ItemCategories = 4
}

const itemTypes = ['Inventory', 'Services', 'Non-Inventory'];

export default function DynamicsApiDemo() {
    const [selectedTab, setSelectedTab] =
        useState<HeaderTabType>(HeaderTabType.Items);

    const [items, setItems] =
        useState<DynamicsItem[]>();

    const [selectedItem, setSelectedItem] =
        useState<DynamicsItem>();

    const [newItem, setNewItem] =
        useState<DynamicsItemBase>();

    const [customers, setCustomers] =
        useState<DynamicsCustomer[]>();

    const [selectedCustomer, setSelectedCustomer] =
        useState<DynamicsCustomer>();

    const [newCustomer, setNewCustomer] =
        useState<DynamicsCustomerBase>();

    const [unitsOfMeasure, setUnitsOfMeasure] =
        useState<DynamicsUnitOfMeasure[]>();

    const [selectedUnitOfMeasure, setSelectedUnitOfMeasure] =
        useState<DynamicsUnitOfMeasure>();

    const [itemCategories, setItemCategories] =
        useState<DynamicsItemCategory[]>();

    const [selectedItemCategory, setSelectedItemCategory] =
        useState<DynamicsItemCategory>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        getAllData();
    }, []);

    return (
        <div className="pw-content">
            <div className="lq-title-bar">
                <div className="lq-main-title">MS Dynamics API Demo</div>
            </div>

            <div>
                <Tabs
                    id="withText"
                    onSelectedIndexChange={onSelectedTabIndexChange}
                    defaultSelectedIndex={0}
                    dataSource={tabsConfig}
                    scrollByContent={true}
                    showNavButtons={true}
                    orientation="horizontal"
                    stylingMode="primary"
                    width={500}
                    iconPosition="start">
                </Tabs>
            </div>

            {selectedTab === HeaderTabType.Items &&
                <div id="azure-relay-datagrid-container" className="pw-datagrid">
                    <DataGrid id="azure-relay-datagrid"
                        dataSource={items ? items : []}
                        showRowLines={true}
                        showBorders={true}
                        height="20rem" width="100%"
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="id"
                        rowAlternationEnabled={true}
                        onSelectionChanged={onItemsSelectionChanged}
                        onInitNewRow={onInitNewItemRow}
                        onRowInserting={onItemRowInserting}
                        onRowInserted={onItemRowInserted}
                        onRowUpdating={onItemRowUpdating} >

                        <Paging defaultPageSize={30} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />
                        <Selection mode='single' />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <SearchPanel visible={true} />

                        <Column type="buttons" width='3rem' >
                            <ColumnButton name="edit" />
                        </Column>

                        <Column dataField="id" dataType="string" width="17rem" allowEditing={false} />
                        <Column dataField="number" dataType="string" width="7rem" />
                        <Column dataField="displayName" dataType="string" width="8rem" />
                        <Column dataField="displayName2" dataType="string" width="8rem" />
                        <Column dataField="type" dataType="string" width="7rem">
                            <Lookup dataSource={itemTypes} />
                        </Column>

                        <Column dataField="itemCategoryId" dataType="string" width="17rem">
                            <Lookup dataSource={itemCategories}
                                valueExpr="id"
                                displayExpr="displayName" />
                        </Column>

                        <Column dataField="itemCategoryCode" dataType="string" width="10rem" allowEditing={false} />

                        <Column dataField="baseUnitOfMeasureId" dataType="string" width="17rem">
                            <Lookup dataSource={unitsOfMeasure}
                                valueExpr="id"
                                displayExpr="displayName" />
                        </Column>

                        <Column dataField="baseUnitOfMeasureCode" dataType="string" width="12rem" allowEditing={false} />
                        <Column dataField="gtin" dataType="string" width="7rem" allowEditing={false} />
                        <Column dataField="inventory" dataType="number" width="7rem" allowEditing={false} />
                        <Column dataField="unitPrice" dataType="number" width="6rem" allowEditing={false} />
                        <Column dataField="unitCost" dataType="number" width="6rem" allowEditing={false} />
                        <Column dataField="generalProductPostingGroupId" dataType="string" width="17rem" allowEditing={false} />
                        <Column dataField="generalProductPostingGroupCode" dataType="string" width="12rem" allowEditing={false} />
                        <Column dataField="inventoryPostingGroupId" dataType="string" width="17rem" allowEditing={false} />
                        <Column dataField="inventoryPostingGroupCode" dataType="string" width="12rem" allowEditing={false} />
                        <Column dataField="blocked" dataType="boolean" width="6rem" allowEditing={false} />
                        <Column dataField="lastModifiedDateTime" dataType="datetime" allowEditing={false}
                            format="MM/dd/yyyy HH:mm:ss" width="12rem" />
                        <Column allowEditing={false} />

                        <Toolbar>
                            <ToolbarItem location="before">
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Items</div>
                                </div>
                            </ToolbarItem>
                            <ToolbarItem location="after">
                                <div className="pw-form-unit">
                                    <Button text="" icon="refresh" type="normal" onClick={getItems} />
                                </div>
                            </ToolbarItem>
                            <ToolbarItem location="after" name="addRowButton" />
                        </Toolbar>

                        <Editing mode="popup" useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={false} />

                    </DataGrid>


                    <div>
                        <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">Insert Item</div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-20">
                                <TextBox id="number" label="Number" labelMode="floating"
                                    placeholder='NA'
                                    value={newItem && newItem.number ? newItem.number : null}
                                    onValueChanged={(e) => setNewItem({ ...newItem, number: e.value })} />
                            </div>

                            <div className="pw-form-unit pw-comp-40">
                                <TextBox id="displayName" label="Display Name" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newItem && newItem.displayName ? newItem.displayName : null}
                                    onValueChanged={(e) => setNewItem({ ...newItem, displayName: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-20">
                                <SelectBox id="itemType" label="Type" labelMode="floating"
                                    placeholder='NA' dataSource={itemTypes}
                                    value={newItem && newItem.type ? newItem.type : null}
                                    onValueChanged={(e) => setNewItem({ ...newItem, type: e.value })} />
                            </div>

                            <div className="pw-form-unit pw-comp-40">
                                <TextBox id="itemCategoryId" label="Item Category" labelMode="floating"
                                    placeholder='NA' height="1.6rem" disabled={true}
                                    value={newItem && newItem.itemCategoryId ? newItem.itemCategoryId : null}
                                    onValueChanged={(e) => setNewItem({ ...newItem, itemCategoryId: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-20">
                                <SelectBox id="baseUnitOfMeasureId" label="Unit of Measure" labelMode="floating"
                                    placeholder='NA' dataSource={unitsOfMeasure}
                                    valueExpr="id" displayExpr="code"
                                    value={newItem && newItem.baseUnitOfMeasureId ? newItem.baseUnitOfMeasureId : null}
                                    onValueChanged={(e) => setNewItem({ ...newItem, baseUnitOfMeasureId: e.value })} />

                            </div>
                        </div>

                        <div className="pw-comp-row pw-margin-top-1">
                            <div className="pw-form-unit">
                                <Button type="default"
                                    text="Insert Item" icon="save"
                                    onClick={onInsertNewItemClick} />
                            </div>
                        </div>
                    </div>

                </div>

            }

            {selectedTab === HeaderTabType.Customers &&
                <div id="azure-relay-datagrid-container" className="pw-datagrid">
                    <DataGrid id="azure-relay-datagrid"
                        dataSource={customers ? customers : []}
                        showRowLines={true}
                        showBorders={true}
                        height="30rem" width="100%"
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="id"
                        rowAlternationEnabled={true}
                        onSelectionChanged={onCustomersSelectionChanged}>

                        <Paging defaultPageSize={30} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />
                        <Selection mode='single' />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <SearchPanel visible={true} />

                        <Column type="buttons" width='3rem' >
                            <ColumnButton name="delete" icon="trash" />
                        </Column>

                        <Column dataField="id" dataType="string" width="17rem" />
                        <Column dataField="displayName" dataType="string" width="10rem" />
                        <Column dataField="addressLine1" dataType="string" width="12rem" />
                        <Column dataField="addressLine2" dataType="string" width="10rem" />
                        <Column dataField="city" dataType="string" width="8rem" />
                        <Column dataField="state" dataType="string" width="6rem" />
                        <Column dataField="postalCode" dataType="string" width="6rem" />
                        <Column dataField="email" dataType="string" width="12rem" />
                        <Column dataField="website" dataType="string" width="12rem" />

                        <Column dataField="lastModifiedDateTime" dataType="datetime"
                            format="MM/dd/yyyy HH:mm:ss" width="12rem" />
                        <Column />

                        <Toolbar>
                            <ToolbarItem location="before">
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Customers</div>
                                </div>
                            </ToolbarItem>
                            <ToolbarItem location="after">
                                <div className="pw-form-unit">
                                    <Button text="" icon="refresh" type="normal" onClick={getCustomers} />
                                </div>
                            </ToolbarItem>

                        </Toolbar>

                    </DataGrid>

                    <div>
                        <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">Insert Customer</div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-20">
                                <TextBox id="displayName" label="Display Name" labelMode="floating"
                                    placeholder='NA'
                                    value={newCustomer && newCustomer.displayName ? newCustomer.displayName : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, displayName: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-40">
                                <TextBox id="addressLine1" label="Address Line 1" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.addressLine1 ? newCustomer.addressLine1 : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, addressLine1: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-40">
                                <TextBox id="addressLine2" label="Address Line 2" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.addressLine2 ? newCustomer.addressLine2 : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, addressLine2: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <TextBox id="city" label="City" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.city ? newCustomer.city : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, city: e.value })} />
                            </div>

                            <div className="pw-form-unit pw-comp-10">
                                <TextBox id="state" label="State" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.state ? newCustomer.state : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, state: e.value })} />
                            </div>

                            <div className="pw-form-unit pw-comp-10">
                                <TextBox id="postalCode" label="Postal Code" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.postalCode ? newCustomer.postalCode : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, postalCode: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-40">
                                <TextBox id="email" label="Email" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.email ? newCustomer.email : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, email: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-40">
                                <TextBox id="website" label="Website" labelMode="floating"
                                    placeholder='NA' height="1.6rem"
                                    value={newCustomer && newCustomer.website ? newCustomer.website : null}
                                    onValueChanged={(e) => setNewCustomer({ ...newCustomer, website: e.value })} />
                            </div>
                        </div>

                        <div className="pw-comp-row pw-margin-top-1">
                            <div className="pw-form-unit">
                                <Button type="default"
                                    text="Insert Customer" icon="save"
                                    onClick={onInsertNewCustomerClick} />
                            </div>
                        </div>
                    </div>
                </div>
            }


            {selectedTab === HeaderTabType.Uoms &&
                <div id="azure-relay-datagrid-container" className="pw-datagrid">
                    <DataGrid id="azure-relay-datagrid"
                        dataSource={unitsOfMeasure ? unitsOfMeasure : []}
                        showRowLines={true}
                        showBorders={true}
                        height="30rem" width="100%"
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="id"
                        rowAlternationEnabled={true}
                        onSelectionChanged={onUomsSelectionChanged}>

                        <Paging defaultPageSize={30} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />
                        <Selection mode='single' />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <SearchPanel visible={true} />

                        <Column type="buttons" width='3rem' >
                            <ColumnButton name="delete" icon="trash" />
                        </Column>

                        <Column dataField="id" dataType="string" width="17rem" />
                        <Column dataField="code" dataType="string" width="7rem" />
                        <Column dataField="displayName" dataType="string" width="8rem" />
                        <Column dataField="internationalStandardCode" dataType="string" width="8rem" />
                        <Column dataField="symbol" dataType="string" width="7rem" />

                        <Column dataField="lastModifiedDateTime" dataType="datetime"
                            format="MM/dd/yyyy HH:mm:ss" width="12rem" />
                        <Column />

                        <Toolbar>
                            <ToolbarItem location="before">
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Units of Measure</div>
                                </div>
                            </ToolbarItem>
                        </Toolbar>

                    </DataGrid>
                </div>
            }


            {selectedTab === HeaderTabType.ItemCategories &&
                <div id="azure-relay-datagrid-container" className="pw-datagrid">
                    <DataGrid id="azure-relay-datagrid"
                        dataSource={itemCategories ? itemCategories : []}
                        showRowLines={true}
                        showBorders={true}
                        height="30rem" width="100%"
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="id"
                        rowAlternationEnabled={true}
                        onSelectionChanged={onItemCategoriesSelectionChanged}>

                        <Paging defaultPageSize={30} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />
                        <Selection mode='single' />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <SearchPanel visible={true} />

                        <Column type="buttons" width='3rem' >
                            <ColumnButton name="edit" />
                        </Column>

                        <Column dataField="id" dataType="string" width="17rem" />
                        <Column dataField="code" dataType="string" width="7rem" />
                        <Column dataField="displayName" dataType="string" width="8rem" />

                        <Column dataField="lastModifiedDateTime" dataType="datetime"
                            format="MM/dd/yyyy HH:mm:ss" width="12rem" />
                        <Column />

                        <Toolbar>
                            <ToolbarItem location="before">
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Item Categories</div>
                                </div>
                            </ToolbarItem>

                        </Toolbar>

                    </DataGrid>
                </div>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={1200} />
            }

        </div>
    );

    function onSelectedTabIndexChange(e: any) {
        setSelectedTab(e);
    }

    function onItemsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedItem(e.selectedRowsData[0]);
    }

    function onCustomersSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedCustomer(e.selectedRowsData[0]);
    }

    function onOpportunitiesSelectionChanged(e: any) {
        //if (e.selectedRowsData && e.selectedRowsData.length > 0)
        //  setSelectedItem(e.selectedRowsData[0])

    }

    function onUomsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedUnitOfMeasure(e.selectedRowsData[0]);
    }

    function onItemCategoriesSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedItemCategory(e.selectedRowsData[0]);
    }

    function onInitNewItemRow(e: any) {
        e.data.type = "Inventory";
    }

    function onItemRowInserting(e: any) {
        e.cancel = insertItem(e.data)
            .then((insertedRecord) => {
                e.data = insertedRecord;
            });
    }

    function onItemRowUpdating(e: any) {
        const updatedRow = { ...e.oldData, ...e.newData };
        e.cancel = updateItem(updatedRow)
            .then(() => false);
    }

    async function onItemRowInserted(e: any) {
        await getItems();
    }

    async function onInsertNewItemClick() {
        if (newItem) {
            await insertItem(newItem)
                .then(() => {
                    showToast("Item saved successfully", "success");
                    getItems();
                })
        }
    }

    async function onInsertNewCustomerClick() {
        if (newCustomer) {
            await insertCustomer(newCustomer)
                .then(() => {
                    showToast("Customer saved successfully", "success");
                    getCustomers();
                })
        }
    }

    async function getItems() {
        await CrudFunctions.getData("dynamics/items")
            .then((data) => {
                setItems(data);
            })
            .catch((res) => {
                //showMessage(res, ModalMessageType.Error, "Error while setting unit");
            })
    }

    async function getCustomers() {
        await CrudFunctions.getData("dynamics/customers")
            .then((data) => {
                setCustomers(data);
            })
            .catch((res) => {
                //showMessage(res, ModalMessageType.Error, "Error while setting unit");
            })
    }

    async function getUnitsOfMeasure() {
        await CrudFunctions.getData("dynamics/unitsofmeasure")
            .then((data) => {
                setUnitsOfMeasure(data);
            })
            .catch((res) => {
                //showMessage(res, ModalMessageType.Error, "Error while setting unit");
            })
    }

    async function getItemCategories() {
        await CrudFunctions.getData("dynamics/itemcategories")
            .then((data) => {
                setItemCategories(data);
            })
            .catch((res) => {
                //showMessage(res, ModalMessageType.Error, "Error while setting unit");
            })
    }

    async function insertItem(item: DynamicsItemBase) {
        return await CrudFunctions.postData("dynamics/item", item);
    }

    async function insertCustomer(customer: DynamicsCustomerBase) {
        return await CrudFunctions.postData("dynamics/customer", customer);
    }


    async function updateItem(item: DynamicsItemBase) {
        return await CrudFunctions.putData("dynamics/item", item);
    }

    async function getAllData() {
        await Promise.all([
            getUnitsOfMeasure(),
            getItemCategories()
        ]);

        let initialNewItem: DynamicsItemBase = {
            type: "Inventory"
        }

        setNewItem(initialNewItem);

        let initialNewCustomer: DynamicsCustomerBase = {
            country: "US"
        }

        setNewCustomer(initialNewCustomer);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }
}